import { useCallback, useRef, useEffect } from 'react';
import Logger from '@app/utils/logger';
import { MarketAggregatesState } from './types';
import { VariableType } from '../types/dashboard.types';
import { ChartMode } from '../components/chart-card/types';
import type { MarketZoneId } from '@app/common/static-data/energy-markets';
import { MarketAggregatePoint } from '@app/api/analytics/types';
import { DASHBOARD_TO_API_VARIABLE_MAP } from '../components/chart-card/config';
import {
  transformToChartData,
  addStatisticalMeasures,
} from '../components/chart-card/utils/dataProcessing';
import { fetchModelRunData, generateRunCacheKey, generateSelectionSignature } from './utils';
import { useDemo } from '@app/components/demo/DemoProvider';

// Per-run cache entry for individual model runs
type RunCacheEntry = {
  modelId: string;
  initTime: string;
  label: string;
  data: MarketAggregatePoint[]; // Raw API data for one run
  timestamp: number;
};

const RUN_CACHE = new Map<string, RunCacheEntry>();
const CACHE_PREFIX = 'ma-run-cache-v2:'; // v2 to invalidate old cache format
const CACHE_TTL_MS = 1 * 60 * 1000; // 1 minute

// Load a single run from localStorage
function loadRunFromLocalStorage(cacheKey: string): RunCacheEntry | null {
  try {
    const raw = localStorage.getItem(CACHE_PREFIX + cacheKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as RunCacheEntry;
    if (!parsed || typeof parsed.timestamp !== 'number') return null;
    return parsed;
  } catch {
    return null;
  }
}

// Save a single run to localStorage
function saveRunToLocalStorage(cacheKey: string, entry: RunCacheEntry): void {
  try {
    localStorage.setItem(CACHE_PREFIX + cacheKey, JSON.stringify(entry));
  } catch {
    // ignore storage errors (quota exceeded, etc.)
  }
}

// Get a single run from cache with TTL check and lazy deletion
function getRunFromCache(cacheKey: string): RunCacheEntry | null {
  const now = Date.now();

  // Check memory cache first
  const mem = RUN_CACHE.get(cacheKey);
  if (mem) {
    if (now - mem.timestamp <= CACHE_TTL_MS) {
      return mem;
    } else {
      // Stale - remove it (lazy deletion)
      RUN_CACHE.delete(cacheKey);
      localStorage.removeItem(CACHE_PREFIX + cacheKey);
      return null;
    }
  }

  // Check localStorage
  const disk = loadRunFromLocalStorage(cacheKey);
  if (disk) {
    if (now - disk.timestamp <= CACHE_TTL_MS) {
      // Fresh - add to memory cache
      RUN_CACHE.set(cacheKey, disk);
      return disk;
    } else {
      // Stale - remove it
      localStorage.removeItem(CACHE_PREFIX + cacheKey);
      return null;
    }
  }

  return null;
}

// Store a single run in cache
function setRunInCache(cacheKey: string, runData: RunCacheEntry): void {
  RUN_CACHE.set(cacheKey, runData);
  saveRunToLocalStorage(cacheKey, runData);
}

// Delete all stale cache entries (periodic cleanup)
function deleteStaleEntries(): void {
  const now = Date.now();

  // Clean memory cache
  for (const [key, entry] of RUN_CACHE.entries()) {
    if (now - entry.timestamp > CACHE_TTL_MS) {
      RUN_CACHE.delete(key);
    }
  }

  // Clean localStorage (only our cache prefix)
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(CACHE_PREFIX)) {
        const raw = localStorage.getItem(key);
        if (raw) {
          try {
            const entry = JSON.parse(raw) as RunCacheEntry;
            if (now - entry.timestamp > CACHE_TTL_MS) {
              keysToRemove.push(key);
            }
          } catch {
            // Invalid entry - remove it
            keysToRemove.push(key);
          }
        }
      }
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key));
  } catch {
    // Ignore errors during cleanup
  }
}

export const useChartDataOperations = (
  state: MarketAggregatesState,
  setState: React.Dispatch<React.SetStateAction<MarketAggregatesState>>,
) => {
  const { isDemo } = useDemo();
  const latestRequestKeyRef = useRef<string>('');

  const fetchChartData = useCallback(
    async (
      selectedVariable: VariableType,
      selectedZone: MarketZoneId | MarketZoneId[] | null,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      _chartMode: ChartMode,
    ) => {
      const noZoneSelected = Array.isArray(selectedZone)
        ? selectedZone.length === 0
        : !selectedZone;
      if (noZoneSelected || !selectedVariable) {
        setState((prevState) => ({
          ...prevState,
          chartData: {
            ...prevState.chartData,
            data: [],
            error: null,
          },
        }));
        return;
      }

      const selectedModelsWithRuns = state.availableModels.filter(
        (model) => model.isSelected && model.selectedRuns.length > 0,
      );

      if (selectedModelsWithRuns.length === 0) {
        setState((prevState) => ({
          ...prevState,
          chartData: {
            ...prevState.chartData,
            data: [],
            error: null,
          },
        }));
        return;
      }

      // Generate selection signature for this request
      const selectionSignature = generateSelectionSignature(
        selectedVariable,
        selectedZone,
        state.availableModels,
        state.weightType,
        state.maxLeadTimeHours,
        isDemo,
      );

      // Mark this as the latest request
      latestRequestKeyRef.current = selectionSignature;

      // Check if state still matches (with TTL check)
      const now = Date.now();
      const isSignatureMatch = state.chartData.cacheKey === selectionSignature;
      const isCacheFresh = now - state.chartData.timestamp <= CACHE_TTL_MS;
      const hasData = state.chartData.data.length > 0;

      if (isSignatureMatch && isCacheFresh && hasData) {
        // Data is already in state and still fresh
        setState((prev) => ({
          ...prev,
          chartData: {
            ...prev.chartData,
            loading: false,
            error: null,
          },
        }));
        return;
      }

      setState((prevState) => ({
        ...prevState,
        chartData: {
          ...prevState.chartData,
          loading: true,
          error: null,
        },
      }));

      try {
        const apiVariableId = DASHBOARD_TO_API_VARIABLE_MAP[selectedVariable];
        if (!apiVariableId) {
          throw new Error(`No API variable mapping found for ${selectedVariable}`);
        }

        // Step 1: Build list of all required runs and check cache for each
        const cachedRuns: Array<{
          modelId: string;
          initTime: string;
          label: string;
          data: MarketAggregatePoint[];
        }> = [];
        const runsToFetch: Array<{
          modelId: string;
          initTime: string;
          label: string;
          cacheKey: string;
        }> = [];

        selectedModelsWithRuns.forEach((model) => {
          model.selectedRuns.forEach((run) => {
            const initTime = `${run.date}T${run.time}:00:00.000Z`;
            const label = `${model.label} | ${run.date} ${run.time}:00`;

            // Generate cache key for this specific run
            const runCacheKey = generateRunCacheKey(
              selectedVariable,
              selectedZone,
              model.id,
              initTime,
              state.weightType,
              state.maxLeadTimeHours,
              isDemo,
            );

            // Check if this run is in cache
            const cachedRun = getRunFromCache(runCacheKey);
            if (cachedRun) {
              // Use cached data
              cachedRuns.push({
                modelId: cachedRun.modelId,
                initTime: cachedRun.initTime,
                label: cachedRun.label,
                data: cachedRun.data,
              });
            } else {
              // Need to fetch this run
              runsToFetch.push({
                modelId: model.id,
                initTime,
                label,
                cacheKey: runCacheKey,
              });
            }
          });
        });

        // Step 2: Fetch only non-cached runs from API
        const fetchPromises = runsToFetch.map((run) =>
          fetchModelRunData(
            run.modelId,
            run.initTime,
            selectedZone as any,
            state.weightType,
            state.maxLeadTimeHours,
            isDemo,
          ).then((data) => ({
            modelId: run.modelId,
            initTime: run.initTime,
            label: run.label,
            data,
            cacheKey: run.cacheKey,
          })),
        );

        const freshlyFetchedRuns = await Promise.all(fetchPromises);

        // Step 3: Cache each newly fetched run individually
        const timestamp = Date.now();
        freshlyFetchedRuns.forEach((run) => {
          const runEntry: RunCacheEntry = {
            modelId: run.modelId,
            initTime: run.initTime,
            label: run.label,
            data: run.data,
            timestamp,
          };
          setRunInCache(run.cacheKey, runEntry);
        });

        // Step 4: Combine cached + fresh run data
        const allRunsData = [
          ...cachedRuns,
          ...freshlyFetchedRuns.map(({ modelId, initTime, label, data }) => ({
            modelId,
            initTime,
            label,
            data,
          })),
        ];

        // Step 5: Transform combined data to chart format
        const chartData = transformToChartData(allRunsData, apiVariableId);
        const totalRuns = allRunsData.length;

        // Set available baselines (all run labels)
        const runLabels = allRunsData.map((run) => run.label);

        // Add statistical measures to base data
        addStatisticalMeasures(chartData, totalRuns, runLabels);

        // Only update state if this is still the latest request
        if (latestRequestKeyRef.current === selectionSignature) {
          setState((prevState) => ({
            ...prevState,
            chartData: {
              data: chartData,
              loading: false,
              error: null,
              availableBaselines: runLabels,
              cacheKey: selectionSignature,
              timestamp: Date.now(),
            },
          }));
        }
      } catch (err: any) {
        // Only set error if this is still the latest request
        if (latestRequestKeyRef.current === selectionSignature) {
          setState((prevState) => ({
            ...prevState,
            chartData: {
              ...prevState.chartData,
              loading: false,
              error: err.message || 'Failed to fetch chart data',
            },
          }));
        }
        Logger.error('Failed to fetch chart data', { error: err, selectedVariable, selectedZone });
      }
    },
    // Depend on state slices that affect API data and cache checks
    [
      state.availableModels,
      state.weightType,
      state.maxLeadTimeHours,
      state.chartData.cacheKey,
      state.chartData.timestamp,
      state.chartData.data.length,
      setState,
      isDemo,
    ],
  );

  const clearChartData = useCallback(() => {
    setState((prevState) => ({
      ...prevState,
      chartData: {
        data: [],
        loading: false,
        error: null,
        availableBaselines: [],
        cacheKey: '',
        timestamp: 0,
      },
    }));
  }, [setState]);

  // Periodic cleanup of stale cache entries
  useEffect(() => {
    // Run cleanup every 5 minutes
    const cleanupInterval = setInterval(
      () => {
        deleteStaleEntries();
      },
      10 * 60 * 1000,
    ); // 5 minutes

    // Run initial cleanup on mount
    deleteStaleEntries();

    return () => clearInterval(cleanupInterval);
  }, []);

  return {
    fetchChartData,
    clearChartData,
  };
};
